import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getUser, clearSession } from "@/lib/api";
import { toast } from "sonner";
import BrandHeader from "@/components/BrandHeader";
import { ArrowLeft, User, Lock, LogOut, ShieldCheck, Check, Loader2 } from "lucide-react";

export default function Account() {
  const navigate = useNavigate();
  const user = getUser();
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user) {
    navigate("/login?next=/cuenta");
    return null;
  }

  async function changePassword(e) {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Mínimo 8 caracteres");
    if (pwd !== pwd2) return toast.error("Las contraseñas no coinciden");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      toast.success("Contraseña actualizada");
      setCurrent(""); setPwd(""); setPwd2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No pudimos actualizar la contraseña");
    } finally {
      setSaving(false);
    }
  }

  function logout() {
    clearSession();
    toast.success("Sesión cerrada");
    navigate("/");
  }

  const hasThc = (user.products || []).includes("thc");
  const hasConsulting = (user.products || []).includes("consulting");

  return (
    <>
      <BrandHeader />
      <div
        className="min-h-screen py-8 md:py-12 px-4 md:px-6 overflow-x-hidden"
        style={{
          background:
            "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
            "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
            "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
        }}
        data-testid="account-page"
      >
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/dashboard")} className="text-sm font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-6 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" /> Volver al dashboard
          </button>

          {/* Hero */}
          <div
            className="relative rounded-3xl p-6 md:p-8 mb-6 overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
              boxShadow: "0 30px 60px -30px rgba(11,61,46,0.55), 0 0 0 1px rgba(201,168,76,0.15) inset",
            }}
          >
            <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
            <div className="relative flex items-start gap-5 flex-wrap">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", boxShadow: "0 8px 22px -6px rgba(201,168,76,0.55)" }}>
                <User className="w-7 h-7" style={{ color: "#0b3d2e" }} strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold mb-3 px-3 py-1 rounded-full" style={{ background: "rgba(201,168,76,0.15)", color: "#E5C874" }}>
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: "#C9A84C", display: "inline-block" }} />
                  Mi cuenta
                </div>
                <h1 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight break-all" style={{ letterSpacing: "-0.02em" }}>
                  {user.email}
                </h1>
                <div className="flex flex-wrap gap-2 mt-3">
                  {hasThc && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.10)", color: "#E5C874", border: "1px solid rgba(201,168,76,0.35)" }}>
                      <Check className="w-3 h-3" strokeWidth={2.5} /> Diagnóstico 47€
                    </span>
                  )}
                  {hasConsulting && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)", color: "#0b3d2e" }}>
                      <ShieldCheck className="w-3 h-3" strokeWidth={2.5} /> Consultoría 397€
                    </span>
                  )}
                  {user.is_admin && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.25)" }}>Admin</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cambio contraseña */}
          <div className="rounded-3xl p-6 md:p-8 mb-6" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}>
            <div className="flex items-center gap-3 mb-5">
              <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>Cambiar contraseña</div>
            </div>
            <form onSubmit={changePassword} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Contraseña actual</label>
                <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 focus:outline-none" data-testid="account-current-password" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Nueva contraseña</label>
                <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={8} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 focus:outline-none" data-testid="account-new-password" />
                <div className="text-[11px] text-slate-400 mt-1">Mínimo 8 caracteres</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">Repite la nueva contraseña</label>
                <input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} required className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-[#0F6E56] focus:ring-2 focus:ring-[#0F6E56]/15 focus:outline-none" data-testid="account-new-password-repeat" />
              </div>
              <button type="submit" disabled={saving} className="rounded-full px-6 py-2.5 text-sm font-semibold text-white inline-flex items-center gap-2 disabled:opacity-50 transition-all hover:-translate-y-0.5" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)", boxShadow: "0 8px 22px -8px rgba(11,61,46,0.5)" }} data-testid="account-save-password">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {saving ? "Guardando…" : "Actualizar contraseña"}
              </button>
            </form>
          </div>

          {/* Cerrar sesión */}
          <div className="rounded-3xl p-6 md:p-8" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 10px 30px -18px rgba(11,61,46,0.18), 0 0 0 1px rgba(11,61,46,0.06)" }}>
            <div className="flex items-center gap-3 mb-3">
              <span style={{ width: 24, height: 1.5, background: "#C9A84C", display: "inline-block" }} />
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#0b3d2e" }}>Sesión</div>
            </div>
            <p className="text-sm text-slate-500 mb-4">Cierra tu sesión en este dispositivo.</p>
            <button onClick={logout} className="rounded-full px-5 py-2 text-sm font-semibold text-slate-700 inline-flex items-center gap-2 transition hover:bg-slate-50" style={{ border: "1px solid #e2e8f0" }} data-testid="account-logout-btn">
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
